import { collection, addDoc, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../config';

export interface ExcelNotification {
  id?: string;
  userId: string;
  type: 'EXCEL_UPDATE' | 'EXCEL_ASSIGNMENT' | 'EXCEL_COMMENT';
  title: string;
  message: string;
  data: {
    sheetName: string;
    itemId: string;
    fieldName?: string;
    fieldValue?: string;
    newValue?: any;
    updatedBy?: string;
    assignedBy?: string;
    comment?: string;
    commentedBy?: string;
    timestamp: string;
    cellAddress?: string;
    row?: number;
    column?: number;
  };
  read: boolean;
  createdAt: string;
}

export class ExcelNotificationService {
  private static instance: ExcelNotificationService;
  private notifications: ExcelNotification[] = [];

  public static getInstance(): ExcelNotificationService {
    if (!ExcelNotificationService.instance) {
      ExcelNotificationService.instance = new ExcelNotificationService();
    }
    return ExcelNotificationService.instance;
  }

  /**
   * Send notification to user
   */
  async sendNotification(notification: Omit<ExcelNotification, 'id' | 'read' | 'createdAt'>): Promise<void> {
    try {
      console.log('🔔 ExcelNotificationService: Sending notification:', notification);
      console.log('🔔 ExcelNotificationService: User ID:', notification.userId);
      console.log('🔔 ExcelNotificationService: Notification type:', notification.type);
      
      const notificationData: Omit<ExcelNotification, 'id'> = {
        ...notification,
        read: false,
        createdAt: new Date().toISOString()
      };
      
      console.log('🔔 ExcelNotificationService: Prepared notification data:', notificationData);

      // Add to Firestore with timeout
      console.log('🔔 ExcelNotificationService: Attempting to add document to Firestore...');
      console.log('🔔 ExcelNotificationService: Collection: excelNotifications');
      console.log('🔔 ExcelNotificationService: Document data keys:', Object.keys(notificationData));
      
      const firestorePromise = addDoc(collection(db, 'excelNotifications'), notificationData);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Firestore operation timeout after 10 seconds')), 10000)
      );
      
      const docRef = await Promise.race([firestorePromise, timeoutPromise]) as any;
      
      console.log('🔔 ExcelNotificationService: Document created with ID:', docRef.id);
      console.log('🔔 ExcelNotificationService: Document reference:', docRef);
      console.log('🔔 ExcelNotificationService: Full notification data saved:', notificationData);
      
      // Add to local cache
      this.notifications.push({
        ...notificationData,
        id: docRef.id
      });

      console.log(`✅ Excel notification created with ID: ${docRef.id}`, notificationData);
      console.log(`✅ ExcelNotificationService: Total notifications in cache: ${this.notifications.length}`);
    } catch (error) {
      console.error('❌ Error sending Excel notification:', error);
      console.error('❌ Error details:', error instanceof Error ? error.message : String(error));
      console.error('❌ Stack trace:', error instanceof Error ? error.stack : 'No stack trace');
      
      // If it's a quota exceeded error or timeout, store locally as fallback
      if (error instanceof Error && (error.message.includes('quota exceeded') || error.message.includes('timeout'))) {
        console.warn('⚠️ Firebase quota exceeded - storing notification locally as fallback');
        
        // Store in local storage as fallback
        try {
          const localNotifications = JSON.parse(localStorage.getItem('excelNotifications') || '[]');
          const localNotification = {
            ...notification,
            read: false,
            createdAt: new Date().toISOString(),
            id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            isLocal: true
          };
          localNotifications.push(localNotification);
          localStorage.setItem('excelNotifications', JSON.stringify(localNotifications));
          
          // Add to local cache
          this.notifications.push(localNotification);
          
          console.log('✅ Notification stored locally as fallback:', localNotification.id);
        } catch (localError) {
          console.error('❌ Failed to store notification locally:', localError);
        }
      } else {
        // For other errors, also try local storage as fallback
        console.warn('⚠️ Firestore error - storing notification locally as fallback');
        try {
          const localNotifications = JSON.parse(localStorage.getItem('excelNotifications') || '[]');
          const localNotification = {
            ...notification,
            read: false,
            createdAt: new Date().toISOString(),
            id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            isLocal: true
          };
          localNotifications.push(localNotification);
          localStorage.setItem('excelNotifications', JSON.stringify(localNotifications));
          
          // Add to local cache
          this.notifications.push(localNotification);
          
          console.log('✅ Notification stored locally as fallback:', localNotification.id);
        } catch (localError) {
          console.error('❌ Failed to store notification locally:', localError);
        }
      }
      
      // Don't throw the error - we don't want to break the user experience
      // The notification will be retried on the next attempt
    }
  }

  /**
   * Get notifications for user
   */
  async getNotifications(userId: string): Promise<ExcelNotification[]> {
    try {
      console.log(`🔔 Getting notifications for user: ${userId}`);
      const q = query(
        collection(db, 'excelNotifications'),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      const notifications = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ExcelNotification));

      // Also get local notifications as fallback
      try {
        const localNotifications = JSON.parse(localStorage.getItem('excelNotifications') || '[]');
        const userLocalNotifications = localNotifications.filter((n: any) => n.userId === userId);
        notifications.push(...userLocalNotifications);
        console.log(`📱 Found ${userLocalNotifications.length} local notifications for user ${userId}`);
      } catch (localError) {
        console.warn('⚠️ Could not load local notifications:', localError);
      }

      // Sort by creation date (newest first)
      notifications.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      console.log(`🔔 Found ${notifications.length} total notifications for user ${userId}:`, notifications);
      this.notifications = notifications;
      return notifications;
    } catch (error) {
      console.error('❌ Error getting Excel notifications:', error);
      
      // Fallback to local notifications only
      try {
        const localNotifications = JSON.parse(localStorage.getItem('excelNotifications') || '[]');
        const userLocalNotifications = localNotifications.filter((n: any) => n.userId === userId);
        console.log(`📱 Using ${userLocalNotifications.length} local notifications as fallback`);
        return userLocalNotifications;
      } catch (localError) {
        console.error('❌ Failed to load local notifications:', localError);
        return [];
      }
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    try {
      const docRef = doc(db, 'excelNotifications', notificationId);
      await updateDoc(docRef, { read: true });
      
      // Update local cache
      const notification = this.notifications.find(n => n.id === notificationId);
      if (notification) {
        notification.read = true;
      }
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
    }
  }

  /**
   * Mark all notifications as read for user
   */
  async markAllAsRead(userId: string): Promise<void> {
    try {
      const notifications = await this.getNotifications(userId);
      const unreadNotifications = notifications.filter(n => !n.read);
      
      await Promise.all(
        unreadNotifications.map(notification => 
          this.markAsRead(notification.id!)
        )
      );
    } catch (error) {
      console.error('❌ Error marking all notifications as read:', error);
    }
  }

  /**
   * Send Excel update notification
   */
  async sendExcelUpdateNotification(
    userId: string,
    sheetName: string,
    itemId: string,
    fieldName: string,
    newValue: any,
    updatedBy: string,
    cellAddress?: string,
    row?: number,
    column?: number
  ): Promise<void> {
    await this.sendNotification({
      userId,
      type: 'EXCEL_UPDATE',
      title: `${sheetName} Updated`,
      message: `${fieldName} was updated in ${sheetName} - ${cellAddress || `Row ${row}, Column ${column}`}`,
      data: {
        sheetName,
        itemId,
        fieldName,
        fieldValue: String(newValue),
        newValue,
        updatedBy,
        timestamp: new Date().toISOString(),
        cellAddress,
        row,
        column
      }
    });
  }

  /**
   * Send Excel assignment notification
   */
  async sendExcelAssignmentNotification(
    userId: string,
    sheetName: string,
    itemId: string,
    assignedBy: string,
    cellAddress?: string,
    fieldName?: string,
    fieldValue?: string
  ): Promise<void> {
    console.log('🔔 ExcelNotificationService: sendExcelAssignmentNotification called with:', {
      userId,
      sheetName,
      itemId,
      assignedBy,
      cellAddress,
      fieldName,
      fieldValue
    });

    // Validate required parameters
    if (!userId || !sheetName || !itemId || !assignedBy) {
      console.error('❌ ExcelNotificationService: Missing required parameters:', {
        userId: !!userId,
        sheetName: !!sheetName,
        itemId: !!itemId,
        assignedBy: !!assignedBy
      });
      return;
    }

    const title = fieldName ? `Assigned to ${fieldName} in ${sheetName}` : `Assigned to ${sheetName}`;
    console.log('🔔 ExcelNotificationService: Generated title:', title);
    const message = fieldName 
      ? `You have been assigned to "${fieldName}" in ${sheetName} - ${cellAddress || itemId}`
      : `You have been assigned to an item in ${sheetName} - ${cellAddress || itemId}`;
    
    console.log('🔔 ExcelNotificationService: Prepared notification:', { title, message });
    
    console.log('🔔 ExcelNotificationService: About to send notification to Firestore...');
    
    try {
      await this.sendNotification({
        userId,
        type: 'EXCEL_ASSIGNMENT',
        title,
        message,
        data: {
          sheetName,
          itemId,
          assignedBy,
          fieldName,
          fieldValue,
          timestamp: new Date().toISOString(),
          cellAddress
        }
      });
      
      console.log('✅ ExcelNotificationService: Notification sent to Firestore successfully');
    } catch (error) {
      console.error('❌ ExcelNotificationService: Failed to send notification to Firestore:', error);
      throw error;
    }
  }

  /**
   * Send dynamic field assignment notification
   */
  async sendDynamicFieldAssignmentNotification(
    userId: string,
    sheetName: string,
    itemId: string,
    fieldName: string,
    fieldValue: string,
    assignedBy: string,
    cellAddress?: string,
    row?: number,
    column?: number
  ): Promise<void> {
    console.log(`🔔 sendDynamicFieldAssignmentNotification called with:`, {
      userId,
      sheetName,
      itemId,
      fieldName,
      fieldValue,
      assignedBy,
      cellAddress,
      row,
      column
    });
    
    try {
      const notificationData = {
        userId,
        type: 'EXCEL_ASSIGNMENT' as const,
        title: `Assigned to ${fieldName} in ${sheetName}`,
        message: `You have been assigned to "${fieldName}" field in ${sheetName} - ${cellAddress || `Row ${row}, Column ${column}`}`,
        data: {
          sheetName,
          itemId,
          fieldName,
          fieldValue,
          assignedBy,
          timestamp: new Date().toISOString(),
          cellAddress,
          row,
          column
        }
      };
      
      console.log(`🔔 Prepared notification data:`, notificationData);
      
      await this.sendNotification(notificationData);
      
      console.log(`✅ Dynamic field assignment notification sent successfully to user ${userId}`);
    } catch (error) {
      console.error(`❌ Error in sendDynamicFieldAssignmentNotification:`, error);
      console.error(`❌ Error details:`, error instanceof Error ? error.message : String(error));
      console.error(`❌ Stack trace:`, error instanceof Error ? error.stack : 'No stack trace');
      throw error; // Re-throw to let caller handle it
    }
  }

  /**
   * Send Excel comment notification
   */
  async sendExcelCommentNotification(
    userId: string,
    sheetName: string,
    itemId: string,
    comment: string,
    commentedBy: string,
    cellAddress?: string
  ): Promise<void> {
    await this.sendNotification({
      userId,
      type: 'EXCEL_COMMENT',
      title: `New Comment in ${sheetName}`,
      message: `${commentedBy} commented: "${comment}"`,
      data: {
        sheetName,
        itemId,
        fieldName: 'Comment',
        fieldValue: comment,
        comment,
        commentedBy,
        timestamp: new Date().toISOString(),
        cellAddress
      }
    });
  }

  /**
   * Get unread notification count for user
   */
  async getUnreadCount(userId: string): Promise<number> {
    try {
      const notifications = await this.getNotifications(userId);
      return notifications.filter(n => !n.read).length;
    } catch (error) {
      console.error('❌ Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Clear all notifications for user
   */
  async clearAllNotifications(userId: string): Promise<void> {
    try {
      const notifications = await this.getNotifications(userId);
      await Promise.all(
        notifications.map(notification => 
          this.markAsRead(notification.id!)
        )
      );
    } catch (error) {
      console.error('❌ Error clearing notifications:', error);
    }
  }

  /**
   * Test notification creation (for debugging)
   */
  async createTestNotification(userId: string): Promise<void> {
    console.log(`🧪 Creating test notification for user: ${userId}`);
    await this.sendNotification({
      userId,
      type: 'EXCEL_ASSIGNMENT',
      title: 'Excel Assignment',
      message: 'You have been assigned a task in Excel',
      data: {
        sheetName: 'Excel Sheet',
        itemId: 'assignment-item',
        fieldName: 'Task Assignment',
        fieldValue: 'New Assignment',
        assignedBy: 'System',
        timestamp: new Date().toISOString()
      }
    });
  }
}

const excelNotificationService = ExcelNotificationService.getInstance();

// Expose test function globally for debugging
if (typeof window !== 'undefined') {
  (window as any).testNotification = (userId: string) => {
    excelNotificationService.createTestNotification(userId);
  };
}

export default excelNotificationService;
